import { type VariantProps, cva } from 'class-variance-authority';
import type * as React from 'react';

import { cn } from '@/lib/utils';

// Tones follow badge.tsx: a ~10% tint, a ~25% border and the icon in the tone. The title stays in
// `fg` and the description in `fg-2`: tone-coloured text on its own light-mode tint is 4.2–4.5:1,
// under AA for 14px, while fg-2 keeps ≥5.8:1 on every tint in both modes.
const alertVariants = cva(
  'relative grid w-full grid-cols-[0_1fr] items-start gap-y-0.5 rounded-card border px-4 py-3 text-fg text-sm has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] has-[>svg]:gap-x-3 [&>svg]:size-4 [&>svg]:translate-y-0.5',
  {
    variants: {
      variant: {
        default: 'border-line-strong bg-raised [&>svg]:text-fg-2',
        info: 'border-label/25 bg-label/8 [&>svg]:text-label',
        ok: 'border-ok/25 bg-ok/8 [&>svg]:text-ok',
        warn: 'border-warn/30 bg-warn/10 [&>svg]:text-warn',
        err: 'border-err/25 bg-err/8 [&>svg]:text-err',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

function Alert({
  className,
  variant = 'default',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      data-variant={variant}
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn('col-start-2 line-clamp-1 min-h-4 font-semibold text-fg', className)}
      {...props}
    />
  );
}

function AlertDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn('col-start-2 grid justify-items-start gap-1 text-fg-2 text-sm [&_p]:leading-relaxed', className)}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription, alertVariants };
